import React, { useState, useCallback, useEffect } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { GanttTask } from '../../api/ganttTasks';
import type { ProjectMilestone } from '../../Data/Milestones/milestoneTypes';
import { deriveMilestoneStatus, newGuid } from '../../Data/Milestones/milestoneTypes';
import {
  getMilestones, addMilestone, updateMilestone, deleteMilestone,
  loadMilestones, MILESTONES_CHANGED_EVENT,
} from '../../Data/Milestones/milestoneOverrides';
import styles from './PerformanceSummary.module.css';

/**
 * MilestoneList — unified milestone display for the Summary side rail.
 *
 * Dual-source:
 *   1. Standalone milestones from milestoneStore (manual, Standard tier)
 *   2. GanttTask milestones (isMilestone === true, Professional tier)
 *
 * Manual milestones support inline add/edit/delete + "mark as met".
 * Gantt-sourced milestones are read-only in this view.
 */

type UnifiedMilestone = {
  id: string;
  name: string;
  targetDate: string | null;
  actualDate: string | null;
  status: 'met' | 'overdue' | 'upcoming';
  source: 'manual' | 'gantt';
  milestoneGuid?: string; // only for manual
};

type Props = {
  projectGuid?: string;
  tasks: GanttTask[];
  canEdit?: boolean;
};

const fmtDate = (iso: string | null): string => {
  if (!iso) return 'TBD';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return iso; }
};

const todayISO = (): string => new Date().toISOString().slice(0, 10);

const MilestoneList: React.FC<Props> = ({ projectGuid, tasks, canEdit = false }) => {
  const { t } = useAppTranslation('kpi');
  const [, forceUpdate] = useState(0);

  // Re-render when store changes
  useEffect(() => {
    const handler = () => forceUpdate((n) => n + 1);
    window.addEventListener(MILESTONES_CHANGED_EVENT, handler);
    return () => window.removeEventListener(MILESTONES_CHANGED_EVENT, handler);
  }, []);

  // Hydrate from API on first render
  useEffect(() => {
    if (projectGuid) loadMilestones(projectGuid);
  }, [projectGuid]);

  // ── Merge sources ──

  const manualMilestones: UnifiedMilestone[] = projectGuid
    ? getMilestones(projectGuid).map((m) => ({
      id: m.milestone_guid,
      name: m.name,
      targetDate: m.target_date,
      actualDate: m.actual_date,
      status: deriveMilestoneStatus(m),
      source: 'manual' as const,
      milestoneGuid: m.milestone_guid,
    }))
    : [];

  const ganttMilestones: UnifiedMilestone[] = tasks
    .filter((tk) => tk.isMilestone)
    .map((tk) => {
      const done = tk.percentComplete >= 100;
      const today = todayISO();
      const overdue = !done && tk.endDate != null && tk.endDate < today;
      return {
        id: `gantt-${tk.id}`,
        name: tk.taskName,
        targetDate: tk.endDate,
        actualDate: done ? (tk.endDate || today) : null,
        status: done ? 'met' as const : overdue ? 'overdue' as const : 'upcoming' as const,
        source: 'gantt' as const,
      };
    });

  const all = [...manualMilestones, ...ganttMilestones].sort((a, b) => {
    // Sort: met last, overdue first, then by target date ascending
    const statusOrder = { overdue: 0, upcoming: 1, met: 2 };
    const diff = statusOrder[a.status] - statusOrder[b.status];
    if (diff !== 0) return diff;
    if (!a.targetDate && !b.targetDate) return 0;
    if (!a.targetDate) return 1;
    if (!b.targetDate) return -1;
    return a.targetDate.localeCompare(b.targetDate);
  });

  // ── Inline add/edit state ──

  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [formDate, setFormDate] = useState('');

  const resetForm = useCallback(() => {
    setAdding(false);
    setEditingId(null);
    setFormName('');
    setFormDate('');
  }, []);

  const handleAdd = useCallback(() => {
    if (!projectGuid || !formName.trim()) return;
    const ms: ProjectMilestone = {
      milestone_guid: newGuid(),
      project_guid: projectGuid,
      name: formName.trim(),
      target_date: formDate || null,
      actual_date: null,
      sort_order: manualMilestones.length,
      source: 'manual',
      gantt_task_id: null,
    };
    addMilestone(projectGuid, ms);
    resetForm();
  }, [projectGuid, formName, formDate, manualMilestones.length, resetForm]);

  const handleEditSave = useCallback(() => {
    if (!projectGuid || !editingId || !formName.trim()) return;
    updateMilestone(projectGuid, editingId, {
      name: formName.trim(),
      target_date: formDate || null,
    });
    resetForm();
  }, [projectGuid, editingId, formName, formDate, resetForm]);

  const handleMarkMet = useCallback((milestoneGuid: string) => {
    if (!projectGuid) return;
    updateMilestone(projectGuid, milestoneGuid, { actual_date: todayISO() });
  }, [projectGuid]);

  const handleDelete = useCallback((milestoneGuid: string) => {
    if (!projectGuid) return;
    deleteMilestone(projectGuid, milestoneGuid);
  }, [projectGuid]);

  const startEdit = useCallback((m: UnifiedMilestone) => {
    setEditingId(m.milestoneGuid || null);
    setFormName(m.name);
    setFormDate(m.targetDate || '');
    setAdding(false);
  }, []);

  // ── Render ──

  const showEmpty = all.length === 0 && !adding;

  return (
    <section className={styles.card} aria-labelledby="milestones-title">
      <div className={styles.cardHeader}>
        <h3 id="milestones-title" className={styles.cardTitle}>
          {t('performanceSummary.milestones.title')}
        </h3>
        {canEdit && projectGuid && !adding && !editingId && (
          <button
            type="button"
            className={styles.editBtn}
            onClick={() => { setAdding(true); setEditingId(null); setFormName(''); setFormDate(''); }}
            aria-label={t('milestones.add')}
          >
            +
          </button>
        )}
      </div>

      {/* Inline add form */}
      {adding && (
        <div style={{ marginBlockEnd: 8 }}>
          <div style={{ display: 'flex', gap: 6, marginBlockEnd: 6, flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder={t('milestones.name')}
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              style={{
                flex: '1 1 140px', fontSize: 13, padding: '4px 8px',
                borderRadius: 6, border: 'var(--border-subtle)',
                background: 'var(--bg-surface)', color: 'var(--text-primary)',
              }}
              autoFocus
            />
            <input
              type="date"
              value={formDate}
              onChange={(e) => setFormDate(e.target.value)}
              style={{
                flex: '0 0 auto', fontSize: 13, padding: '4px 8px',
                borderRadius: 6, border: 'var(--border-subtle)',
                background: 'var(--bg-surface)', color: 'var(--text-primary)',
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <button type="button" className={styles.editorCancel} onClick={resetForm}>
              {t('milestones.cancel')}
            </button>
            <button type="button" className={styles.editorSave} onClick={handleAdd} disabled={!formName.trim()}>
              {t('milestones.save')}
            </button>
          </div>
        </div>
      )}

      {showEmpty && (
        <div className={styles.empty}>{t('milestones.empty')}</div>
      )}

      <div className={styles.milestoneRows}>
        {all.map((m) => {
          const isEditing = editingId === m.milestoneGuid;
          const isManual = m.source === 'manual';
          const dotClass = `${styles.milestoneDot} ${
            m.status === 'met' ? styles.milestoneDone
              : m.status === 'overdue' ? styles.milestoneOverdue
                : ''
          }`;

          if (isEditing && isManual) {
            return (
              <div key={m.id} style={{ marginBlockEnd: 6 }}>
                <div style={{ display: 'flex', gap: 6, marginBlockEnd: 6, flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    style={{
                      flex: '1 1 140px', fontSize: 13, padding: '4px 8px',
                      borderRadius: 6, border: 'var(--border-subtle)',
                      background: 'var(--bg-surface)', color: 'var(--text-primary)',
                    }}
                    autoFocus
                  />
                  <input
                    type="date"
                    value={formDate}
                    onChange={(e) => setFormDate(e.target.value)}
                    style={{
                      flex: '0 0 auto', fontSize: 13, padding: '4px 8px',
                      borderRadius: 6, border: 'var(--border-subtle)',
                      background: 'var(--bg-surface)', color: 'var(--text-primary)',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <button type="button" className={styles.editorCancel} onClick={resetForm}>
                    {t('milestones.cancel')}
                  </button>
                  <button type="button" className={styles.editorSave} onClick={handleEditSave} disabled={!formName.trim()}>
                    {t('milestones.save')}
                  </button>
                </div>
              </div>
            );
          }

          return (
            <div key={m.id} className={styles.milestoneRow}>
              <span className={dotClass} aria-hidden="true" />
              <span className={styles.milestoneName} title={m.name}>
                {m.name}
              </span>
              {m.status === 'met' && m.actualDate && (
                <span style={{ fontSize: 10, color: 'var(--color-success)', fontWeight: 600, flex: '0 0 auto' }}>
                  ✓ {fmtDate(m.actualDate)}
                </span>
              )}
              {m.status !== 'met' && (
                <span className={`${styles.milestoneDate} ${m.status === 'overdue' ? styles.milestoneOverdue : ''}`}>
                  {fmtDate(m.targetDate)}
                </span>
              )}
              {/* Hover-reveal action buttons for manual milestones */}
              {canEdit && isManual && (
                <span className={styles.milestoneActions}>
                  {m.status !== 'met' && (
                    <button
                      type="button"
                      className={styles.milestoneActionBtn}
                      onClick={() => handleMarkMet(m.milestoneGuid!)}
                      title={t('milestones.markMet')}
                      style={{ color: 'var(--color-success)', fontWeight: 600 }}
                    >
                      ✓
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.milestoneActionBtn}
                    onClick={() => startEdit(m)}
                    title={t('milestones.edit')}
                    style={{ color: 'var(--text-tertiary)' }}
                  >
                    ✎
                  </button>
                  <button
                    type="button"
                    className={styles.milestoneActionBtn}
                    onClick={() => handleDelete(m.milestoneGuid!)}
                    title={t('milestones.delete')}
                    style={{ color: 'var(--color-error)' }}
                  >
                    ✕
                  </button>
                </span>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default MilestoneList;
